// Call plugin.
import { OPERATIONS, NO_FINISH_OPS, OP_STATUS, OP_TRANSITIONS } from './constants'

// Lodash FP
import { contains, isNil, property } from 'lodash/fp'

/*
 * Utility function to parse the operation and operation status from an action.
 * @method getOperationMeta
 * @param  {Object}  actionType A call action.
 * @return {Object}  opMeta             Information about the operation.
 * @return {string}  [opMeta.operation] The call operation the action is about.
 * @return {string}  opMeta.transition  The change in operation the action is for.
 * @return {boolean} opMeta.isLocal     Whether the operation was started locally or not.
 * @throws {Error}   Input must be a standard call action type.
 */
export function getOperationMeta (actionType) {
  // Make sure the input is a call action type.
  if (typeof actionType !== 'string' || !actionType.includes('/CALL/')) {
    throw new Error('Invalid input parameter. Must be a Call action.')
  }

  // Grab the unique part of the action type, after /CALL/
  const opType = actionType.match(/\/CALL\/(.*)/)[1]

  /**
   * The operation name can be `OPERATION` or `OPER_ATION` (or even multiple _).
   * It may have the prefix `REMOTE` if it is a remote operation.
   * It may have the prefix `PENDING` if the operation is now waiting on remote.
   * It may have the suffix `FINISH` if the operation has ended.
   * Prefixes / suffixes are separated by a `_`.
   *
   * Parse the operation type to split apart the possible sections. Use those
   *    sections to infer informaton about the operation.
   */
  const opMeta = {}
  const parts = opType.split('_')

  /**
   * Check the parts for prefixes and update opMeta based on whichever are found.
   */
  const validPrefixes = ['REMOTE', 'PENDING']
  const prefixes = []

  // Check the first parts for prefixes.
  while (validPrefixes.includes(parts[0])) {
    // Take the prefix from `parts` and add it to `prefixes`.
    prefixes.push(parts.shift())
  }

  // Handle the 'REMOTE' prefix.
  //    The lack of this prefix means the operation was started locally.
  opMeta.isLocal = !prefixes.includes('REMOTE')

  // Handle the 'PENDING' prefix.
  //    The lack of this prefix means the operation is either starting or ending.
  if (prefixes.includes('PENDING')) {
    opMeta.transition = OP_TRANSITIONS.UPDATE
    /**
     * The `PENDING` prefix is a special-case because it means an operation was
     *    `ongoing` and is changing to `pending`. We know there won't be a
     *    suffix, and the operation is already in state, so we don't need to
     *    find it here (also, it isn't included here).
     */
    return opMeta
  }

  /**
   * Check the parts for suffixes and update opMeta based on whichever are found.
   */
  const validSuffixes = ['FINISH']
  const suffixes = []

  // Check the last parts for suffixes.
  while (validSuffixes.includes(parts[parts.length - 1])) {
    // Take the suffix out of `parts` and add it to `suffixes`.
    suffixes.push(parts.pop())
  }

  // Handle the 'FINISH' suffix.
  //    The lack of this suffix means the operation is just starting (since we
  //    already checked for the `pending` change before this).
  opMeta.transition = suffixes.includes('FINISH') ? OP_TRANSITIONS.FINISH : OP_TRANSITIONS.START

  /**
   * Recombine the remaining parts as the operation.
   */
  opMeta.operation = OPERATIONS[parts.join('_')]
  return opMeta
}

/**
 * Reducer utility: Wraps a call reducer to update a call's operation subsate.
 *    Only affects either `call.localOp` or `call.remoteOp` portion of state.
 * @method setOperationState
 * @param  {Object} state  Redux state of a [single] call.
 * @param  {Object} action A call action.
 */
export function setOperationState (state, action) {
  let meta
  try {
    meta = getOperationMeta(action.type)
  } catch (err) {
    // Action type is not a call operation, don't change operation state.
    return state
  }
  const metaOp = meta.operation

  // The operation has to be defined for start/finish transitions.
  //    Don't update state if it isn't.
  if (!metaOp && meta.transition !== OP_TRANSITIONS.UPDATE) {
    return state
  }

  // Determine which operation substate we should be acting on.
  const stateProp = meta.isLocal ? 'localOp' : 'remoteOp'
  const current = state[stateProp]

  if (meta.transition === OP_TRANSITIONS.START) {
    // A new operation is starting...

    if (current) {
      // ...but there is still an on-going op.
      // The new operation should be rejected, so state should not change.
      return state
    } else {
      // ...and there is no current operation on-going.
      // Set the new operation in state.
      return {
        ...state,
        [stateProp]: {
          operation: metaOp,
          status: OP_STATUS.ONGOING
        }
      }
    }
  } else if (meta.transition === OP_TRANSITIONS.UPDATE) {
    return {
      ...state,
      [stateProp]: {
        ...state[stateProp],
        status: OP_STATUS.PENDING,
        operationData: action.payload.operationData
      }
    }
  } else {
    const isRemote = !meta.isLocal
    const unsetOperation = {
      ...state,
      [stateProp]: undefined
    }

    const currentOp = property('operation')(current)
    if (isNil(currentOp)) {
      // Should be considered an error scenario.
      return state
    }

    // An on-going operation is finishing...
    if (currentOp === metaOp) {
      // The operation matches the current on-going operation.
      //    Unset it in state.
      return unsetOperation
    } else if (isRemote && currentOp === OPERATIONS.SLOW_START) {
      // Special case: If the current operation was a remote slow start,
      //    then the 'finish' won't match. Allow any 'finish' to unset it.
      return unsetOperation
    } else if (contains(currentOp)(NO_FINISH_OPS)) {
      // Special case: If the current operation is one that doesn't have a finish
      //    operation, allow any 'finish' to unset it.
      return unsetOperation
    } else if (metaOp === OPERATIONS.END) {
      // Special-case: If the operation is an "end call" (but the on-going operation
      //    doesn't match), then unset the current operation.
      return unsetOperation
    } else {
      // ...but it wasn't tracked in state?
      // Should be considered an error scenario.
      return state
    }
  }
}
